import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import {
  Image,
  List,
  Header,
  Label,
  Popup,
  Icon,
  Divider,
  Dimmer,
  Accordion,
  Button,
  Menu,
} from 'semantic-ui-react';
import { ReactHTMLConverter } from 'react-html-converter/browser';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  ReservationStatusesEnums,
  ReservationStatusTypes,
} from '../../components/shared/statustypes';
import _ from 'lodash';
import formatCurrency from 'format-currency';
import { getNationalityNameById } from '../../components/shared/nationalities';
import { Images } from '../../assets/assets';
import IFrameDialog from '../../components/shared/iframedialog';
import {
  SendConfirmationEmail,
  UpdateComments,
  RenderTemplateToPdf,
  GetReservationTotals,
  UploadFile,
} from '../../api/services/pms';
import {
  GetFullName,
  getContrastYIQ,
  currencyFormat,
} from '../../components/shared/utils';
import { endpoint } from '../../api/http';
import {
  onSuccess,
  onError,
  onWarning,
} from '../../components/shared/messages';
import { MakePaymentPopup } from '../../components/Payment';
import { PaymentDialog } from '../../components/Payment/paymentDialog';
import { useMessageResources, useTextResources } from '../../language';
import SendMailPopup from '../../components/SendMailPopup';
import { RebuildReservation } from '../../components/shared/reservation';
import ColorPickerExtended from '../../components/shared/ColorPicker/ColorPickerExtended';
import {
  Actions,
  TemplateType,
  HotelFeatureCodes,
  dateTimeFormatSpaced,
  NoteStatusEnum,
} from '../../components/shared/constants';
import HtmlEditorPopup from '../../components/shared/HtmlEditor';
import { isMobileOnly } from 'mobile-device-detect';
import { GetDefaultTemplateBySpecification } from '../../api/services/shared';
import { IssueInvoice } from '../../api/services/finance';
import { Action, Fab } from 'react-tiny-fab';
import ReceiptPopup from '../../components/ReceiptPopup';
import ValidateModalPermission from '../../components/shared/ValidateModalPermission';
import {
  PermissionsActions,
  UserHasPermission,
} from '../../components/shared/permissionsActions';
import CheckTaxExemptionPopup from '../../components/CheckTaxExemptionPopup';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useConfirm } from '../../hooks/useConfirm';
import ApplicationContext from '../../context';
import saveAs from 'file-saver';
import { Reservations } from '../../api/services/next';
import { useNotesToast } from '../../hooks/useNotesToast';
import FolioPendingPopup from '../../components/FolioPendingPopup'
import { useLoader } from '../../hooks/useLoader';
import useBoolean from '../../hooks/useBoolean';
import CancelReservationPopup from '../../components/CancelReservationPopup';
import ExtraAddonsPopup from '../../components/ExtraAddonsPopup';
import ReservationPolicyPopup from '../../components/ReservationPolicyPopup';
import { ReservationAddonAccordion } from '../../components/ReservationAddons/ReservationAddonAccordion';
import TextList from '../../components/shared/TextList';
import { buildAutoDescriptionForPolicy } from '../../components/shared/reservationPolicy';

const html = new ReactHTMLConverter();
let statusChange = null;

const ReservationInfo = ({
  event,
  onCreateUpdate,
  refetchReservations,
  reservationVisible,
  setSelectedTotals,
  onCancelReservationOpenChange,
  ...rest
}) => {
  const loader = useLoader();
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [reservationGroup, setReservationGroup] = useState();
  const [hotel, setHotel] = useState(null);
  const [totals, setTotals] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showCommentsEditor, setShowCommentsEditor] = useState(false);
  const [showGuestCommentsEditor, setShowGuestCommentsEditor] = useState(false);
  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [fabActive, setFabActive] = useState(false);
  const [errorState, setErrorState] = useState({ showError: false });
  const [showReservationDetails, setShowReservationDetails] = useState(() =>
    isMobileOnly ? false : true
  );
  const [isFirstLoad, setIsFirstLoad] = useLocalStorageState(
    'isFirstLoadCalendarReservationDetails',
    true
  );
  const confirm = useConfirm();
  const text = useTextResources();
  const message = useMessageResources()
  const { notes } = useNotesToast({
    reservationId: reservation?.Id,
    hotelId: hotel?.Id,
    reservationNumber: reservation?.Number,
    status: useMemo(() => [NoteStatusEnum.Open], []),
  });
  const [showExtraAddonsPopup, setShowExtraAddonsPopup] = useState(false);
  const reservationPolicyPopupState = useBoolean()
  const cancelReservationPopupState = useBoolean();
  const [showCheckTaxExemptionPopup, setShowCheckTaxExemptionPopup] =
    useState(false);
  const hiddenFileInputRef = useRef(null);
  const mutationObserver = useRef(null);
  const eventRef = useRef(event);
  useEffect(() => { if (event != null) eventRef.current = event; }, [event]);

  const {
    state: { nationalities },
  } = useContext(ApplicationContext);

  const canEdit = window.optima.user
    ? window.optima.user.ActionIDs.includes(Actions.EditCalendar)
    : true;

  useEffect(() => {
    onCancelReservationOpenChange?.(cancelReservationPopupState.value);
  }, [cancelReservationPopupState.value, onCancelReservationOpenChange]);

  useEffect(() => {
    if (event) {
      const reservation = event.extendedProps?.reservation;
      const hotel = event.extendedProps?.hotel;
      const totals = event.extendedProps?.totals;

      setReservation(reservation);
      setHotel(hotel);
      if (reservation?.Id && hotel?.Id) {
        getAndSetGroupReservations({
          reservationId: reservation.Id,
          hotelId: hotel.Id,
        });
      }
      if (totals) {
        setTotals(totals);
      } else if (reservation?.Id && hotel?.Id) {
        (async () => {
          try {
            const totals = await GetReservationTotals({
              ReservationIDs: [reservation.Id],
              HotelId: hotel.Id,
              TargetCurrencyID: reservation && reservation.MainCurrencyId,
            });
            if (totals && totals.length) {
              const totalData = totals[0];
              if (event?.setExtendedProp) {
                event.setExtendedProp('totals', totalData);
              }
              setSelectedTotals(totalData);
              setTotals(totalData);
            }
          } catch (error) {
            onError(error);
          }
        })();
      }
    }
  }, [event]);

  const getAndSetGroupReservations = async ({ hotelId, reservationId }) => {
    try {
      const fetchedReservationGroup = await Reservations.GetReservationGroup({
        HotelId: hotelId,
        ReservationId: reservationId
      });
      setReservationGroup(fetchedReservationGroup)
    } catch (error) {
      onError(error);
    }
  }

  const guestNationality = useMemo(() => {
    const nationalityId = reservation?.Guest?.NationalityId;
    if (nationalityId) {
      return nationalities.find((n) => n.Id === nationalityId);
    }
  }, [nationalities, reservation, reservation?.Guest]);

  let image = Images.male_avatar;

  const updateReservationStatus = async (status, forced) => {
    let clone = _.clone(reservation);
    clone.StatusId = status;
    clone.ForcedStatusUpdate = forced;
    if (forced) {
      statusChange = null;
      //if its day use reservation, set DayUseExecutePostBookingFlag to true
      if (moment(clone.CheckInDate).isSame(clone.CheckOutDate, 'day')) {
        clone.DayUseExecutePostBookingFlag = true;
      }
    }
    try {
      let updated = await rest.updateReservationStatus(clone);
      updated = {
        ...clone,
        Color: updated.Color,
        CheckInDate: updated.CheckInDate,
        CheckOutDate: updated.CheckOutDate,
      };
      setReservation(updated);
      onSuccess();
      return updated;
    } catch (err) {
      if (err.status === 400) {
        if (err.error.IsRevocable) {
          if (err.error.ErrorCode == 10167 || err.error.ErrorCode == 12354 || err.error.ErrorCode == 12367) {
            loader(false);
            setErrorState({
              showError: true,
              error: err.error
            })
          } else {
            loader(false);
            const errorMessageParsed = JSON.parse(err.error.ErrorMessage).message
            const isRevocable = err.error.IsRevocable
            const confirmed = await confirm({
              header: 'Status Could Not Be Changed',
              content: errorMessageParsed,
              confirmButton: {
                content: isRevocable ? 'Yes' : 'Close',
                positive: true,
              },
              cancelButton: {
                content: isRevocable ? 'No' : null,
                negative: true,
              },
            });
            if (confirmed && isRevocable && statusChange) {
              // Show loader before retrying with forced=true
              loader(true);
              await statusChange(true);
            }
          }
        } else {
          onWarning(err);
        }
      } else {
        onError(err);
      }
      return null;
    }
  };
  const cancelReservation = async () => {
    cancelReservationPopupState.setTrue();
  };

  const handleCancelReservation = async (updatedReservation) => {
    cancelReservationPopupState.setFalse()

    if (!updatedReservation) {
      return;
    }

    setReservation(updatedReservation);

    loader(true);
    try {
      if (event) {
        event.remove();
      }

      _.defer(rest.rebuildDayUseEvents());
      _.defer(rest.rebuildVirtualEvents());
    } finally {  
      loader(false);
      rest.closePanel();
    }
  }

  const checkInReservation = async (forced) => {
    loader(true);
    try {
      statusChange = checkInReservation;
      const updated = await updateReservationStatus(
        ReservationStatusesEnums.CheckedIn,
        forced
      );
      if (updated && event) {
        event.setProp('color', updated.Color);
        event.setProp('textColor', getContrastYIQ(updated.Color || '39c2e1'));
        event.setExtendedProp('reservation', updated);
        event.setProp('classNames', ['selected']);
      }
    } finally {
      loader(false);
    }
  };

  const checkOutReservation = async () => {
    const isEarlyCheckout = moment(window.user?.CurrentHotel?.HotelParameter?.CurrentBusinessDate).startOf("day").isBefore(moment(reservation.CheckOutDate).startOf("day"))
    if (reservation.StatusId !== ReservationStatusesEnums.CheckedIn) {
      onWarning(message("EarlyCheckoutIsNotPossible"));
      return
    }
    else if (
      isEarlyCheckout && !(await confirm({
        header: text("EarlyCheckOut"),
        content: `${text("WouldYouLikeTo")} ${text("PerformAnEarlyCheckOutToReservation")} ${text("Number")} ${reservation.Number}?`,
      }))
    )
      return;

    loader(true);
    try {
      RebuildReservation(
        reservation,
        reservation.CheckInDate,
        reservation.CheckOutDate
      );
      statusChange = checkOutReservation;
      const updated = await updateReservationStatus(
        ReservationStatusesEnums.CheckedOut,
        false
      );
      if (updated && event) {
        onCreateUpdate([updated]);
      }
    } finally {
      loader(false);
    }
  };

  const sendConfirmation = async () => {
    if (!UserHasPermission(PermissionsActions.AccessToSendConfirmation)) {
      return onWarning(text('NoPermission'));
    }
    const isSendConfirmation = await confirm({
      header: 'Send Confirmation Email',
      content: `Would you like to send a confirmation email to: ${reservation.Guest.Email}?`,
      confirmButton: {
        content: 'Send',
        loading: isSendingEmail,
        disabled: isSendingEmail,
        positive: true,
      },
      cancelButton: {
        content: 'Cancel',
        negative: true,
        disabled: isSendingEmail,
      },
    });
    if (!isSendConfirmation) return;
    loader(true);
    if (reservation) {
      setIsSendingEmail(true);
      try {
        await SendConfirmationEmail(reservation);
        onSuccess('ConfirmationEmailSent');
      } catch (err) {
        err.status === 400 ? onWarning(err) : onError(err);
      } finally {
        setIsSendingEmail(false);
      }
    }
    loader(false);
  };

  const issueInvoice = async () => {
    try {
      loader(true);
      const result = await IssueInvoice({
        HotelId: reservation.HotelId,
        ReservationIDs: [reservation.Id],
        IsFromFolio: false,
      });
      if (result && result.length) {
        onSuccess('InvoiceWasIssued');
      } else {
        onSuccess('NoOpenInvoiceWasFound');
      }
    } catch (error) {
      onError(error);
    } finally {
      loader(false);
    }
  };

  useEffect(() => {
    if (reservationVisible) {
      const fab = document.getElementById('fab-button');
      if (mutationObserver.current === null && fab !== null) {
        mutationObserver.current = new MutationObserver((mutations) => {
          const mutation = mutations.find(
            (x) => x.type === 'attributes' && x.attributeName == 'class'
          );
          if (mutation) {
            if (mutation.target.classList.contains('open')) {
              setFabActive(true);
            } else if (mutation.target.classList.contains('closed')) {
              setFabActive(false);
            }
          }
        });

        mutationObserver.current.observe(fab, { attributes: true });
      }
    }
  }, [reservationVisible]);

  const onChangeColor = async (color) => {
    const currentEvent = eventRef.current;
    if (currentEvent) {
      try {
        const updated = {
          ...reservation,
          Color: color,
        };
        await Reservations.PatchReservation({
          Id: reservation.Id,
          Color: color,
        });

        if (currentEvent?.setProp) {
          currentEvent.setProp('color', color);
          currentEvent.setProp('textColor', getContrastYIQ(color || '39c2e1'));
          currentEvent.setExtendedProp('reservation', updated);
          currentEvent.setProp('classNames', ['selected']);
        }
        setReservation(updated);
        onSuccess();
      } catch (err) {
        err.status === 400 ? onWarning(err) : onError(err);
      }
    }
  };

  const onSaveGroup = async () => {
    if (refetchReservations) {
      await refetchReservations();
    }
  };

  const refreshTotals = async () => {
    if (!reservation || !hotel) return;
    try {
      const totals = await GetReservationTotals({
        ReservationIDs: [reservation.Id],
        HotelId: hotel.Id,
        TargetCurrencyID: reservation.MainCurrencyId,
      });
      if (totals && totals.length) {
        const totalData = totals[0];
        const currentEvent = eventRef.current;
        if (currentEvent?.setExtendedProp) {
          currentEvent.setExtendedProp('totals', totalData);
        }
        setSelectedTotals?.(totalData);
        setTotals(totalData);
      }
    } catch (error) {
      onError(error);
    }
  };

  const saveComments = async (value) => {
    try {
      await UpdateComments({
        Id: reservation.Id,
        HotelId: reservation.HotelId,
        Comments: value,
      });
      const updated = { ...reservation, Comments: value };
      setReservation(updated);
      onCreateUpdate([updated]);
      setShowCommentsEditor(false);
      onSuccess('CommentsUpdated');
    } catch (error) {
      onError(error);
    }
  };

  const saveGuestComments = async (value) => {
    try {
      await UpdateComments({
        Id: reservation.Id,
        HotelId: reservation.HotelId,
        CommentsVisibleToGuest: value,
      });
      const updated = { ...reservation, CommentsVisibleToGuest: value };
      setReservation(updated);
      onCreateUpdate([updated]);
      setShowGuestCommentsEditor(false);
      onSuccess('MessagesForGuestsUpdated');
    } catch (error) {
      onError(error);
    }
  };

  const printFamilyAndFriendsAction = async () => {
    loader(true);
    var templateSpec = {
      HotelId: reservation.HotelId,
      TemplateTypeId: TemplateType.PartyConfirmation,
    };

    var defaultPartyConfirmationTemplates =
      await GetDefaultTemplateBySpecification(templateSpec);

    if (defaultPartyConfirmationTemplates) {
      try {
        var defaultTemplate = defaultPartyConfirmationTemplates.find(
          (x) => x.LanguageID === reservation.Guest.LanguageID
        );
        if (defaultTemplate === undefined) {
          defaultTemplate = defaultPartyConfirmationTemplates.find(
            (x) => x.LanguageID === null
          );
        }
        if (!defaultTemplate) {
          onError(text('DefaultTemplateDoesNotExists'));
        } else {
          const result = await RenderTemplateToPdf({
            FileName: 'PartyConfirmation' + ' - ' + reservation.Number,
            IsPrintable: true,
            TemplateID: defaultTemplate.TemplateID,
            Paramters: [
              {
                key: 'ReservationId',
                value: reservation ? reservation.Id : null,
              },
              {
                key: 'HotelID',
                value: reservation ? reservation.HotelId : null,
              },
            ],
          });
          window.open(result.DmzUrlPathAccessibleByBrowser);
        }
      } catch (error) {
        onError(error);
      } finally {
        loader(false);
      }
    } else {
      onError('DefaultTemplateNotExists');
    }
  };
  const printReservationSummary = async () => {
    try {
      const templates = await GetDefaultTemplateBySpecification({
        HotelId: reservation.HotelId,
        TemplateTypeId: TemplateType.Confirmation,
      });
      let template =
        templates.find((x) => x.LanguageID === reservation.Guest.LanguageID) ||
        templates.find((x) => x.LanguageID === null);

      if (template) {
        const result = await RenderTemplateToPdf({
          FileName: template
            ? template.TypeName
            : '' + ' - ' + reservation.Number,
          IsPrintable: true,
          TemplateID: template ? template.TemplateID : null,
          Paramters: [
            {
              key: 'ReservationId',
              value: reservation ? reservation.Id : null,
            },
            {
              key: 'HotelID',
              value: reservation ? reservation.HotelId : null,
            },
          ],
        });
        window.open(result.DmzUrlPathAccessibleByBrowser);
      } else {
        onWarning('No default template was found');
      }
    } catch (error) {
      onError(error);
    }
  };
  const openReservationPolicyPopup = () => {
    if (!reservation.ReservationPolicyId) {
      // show warning
      return onWarning(message("ReservationPolicyUnassigned"))
    }

    reservationPolicyPopupState.setTrue();
  }

  const closeReservationPolicyPopup = () => {
    reservationPolicyPopupState.setFalse();
  }

  const handleExtraAddonsClose = async (updatedReservation) => {
    setShowExtraAddonsPopup(false);

    if (!updatedReservation) {
      return;
    }

    setReservation(updatedReservation);
    onCreateUpdate?.([updatedReservation]);

    try {
      const totals = await GetReservationTotals({
        ReservationIDs: [updatedReservation.Id],
        HotelId: updatedReservation.HotelId,
        TargetCurrencyID: updatedReservation.MainCurrencyId,
      });
      if (totals && totals.length) {
        const totalData = totals[0];
        if (event?.setExtendedProp) {
          event.setExtendedProp('totals', totalData);
        }
        setSelectedTotals?.(totalData);
        setTotals(totalData);
      }
    } catch (error) {
      onError(error);
    }
  };

  const printButtonFamilyAndFriends = (
    <Button primary size='small' onClick={printFamilyAndFriendsAction}>
      Print
    </Button>
  );

  if (reservation) {
    let addons = _.uniq(
      _.map(reservation.ReservationDailyAddons, (addon) => {
        return addon.HotelAddon?.ChainAddon?.Name;
      })
    );

    if (reservation.Guest) {
      if (reservation.Guest.Images && reservation.Guest.Images.length > 0) {
        image = `${endpoint + reservation.Guest.Images[0].Path}`;
      } else if (reservation.Guest.Gender === 'F') {
        image = Images.female_avatar;
      }
    }

    const hasCCFeature =
      hotel && hotel.HotelFeatures.find((x) => x.FeatureId === 3);
    const hasCheckTaxExemptionFeature =
      hotel &&
      hotel.HotelFeatures.find(
        (x) => x.FeatureId === HotelFeatureCodes.CheckTaxExemption
      );

    const handleFileUpload = async (event) => {
      event.preventDefault();
      const files = hiddenFileInputRef.current?.files;
      if (files.length > 0) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('EntityTypeID', 1);
          formData.append('EntityID', reservation.Id);
          formData.append(
            'Name',
            window.btoa(unescape(encodeURIComponent(file.name)))
          );
          formData.append(
            'Description',
            window.btoa(unescape(encodeURIComponent(file.name)))
          );
          formData.append('files', file);

          try {
            await UploadFile(formData);
            onSuccess('FileUploadedSuccessfully');
          } catch (error) {
            onError(error);
          }
        }
      }
    };

    const getPhoneForWhatsAppLink = (() => {
      const prefix = guestNationality?.Country?.Prefix;
      const phone = reservation.Guest?.Phone?.replace(/\D/g, '');
      const regex = new RegExp(prefix);
      return new RegExp(regex).test(phone)
        ? `https://wa.me/${phone}`
        : `https://wa.me/${prefix}${phone}`;
    })();

    const reservationDocuments = [];
    if (reservation.Images) {
      reservationDocuments.push(...reservation.Images);
    }
    if (reservation.Guest?.Images) {
      reservationDocuments.push(...reservation.Guest.Images);
    }

    const documentsPanel = [
      {
        key: `documentsPanel`,
        title: text('Documents'),
        content: {
          content: (
            <List>
              {reservationDocuments.map((image) => (
                <List.Item
                  icon='attach'
                  key={image.Id}
                  content={
                    <a
                      className='link-reservation-guest-document'
                      onClick={() => saveAs(image?.Path)}
                    >
                      {image?.Name}
                    </a>
                  }
                />
              ))}
            </List>
          ),
        },
      },
    ];

    const colorPicker = () => (
      <ColorPickerExtended
        onChangeColor={onChangeColor}
        color={reservation.Color}
      >
        {reservation.Color && (
          <Button
            style={{ marginTop: '1em' }}
            onClick={() => onChangeColor(null)}
            size='mini'
            compact
          >
            {text('DefaultColor')}
          </Button>
        )}
      </ColorPickerExtended>
    );

    const reservationDetails = () => (
      <>
        <div
          className='details attached'
          style={
            isMobileOnly ? { marginTop: '1em', overflow: 'visible' } : null
          }
        >
          <table>
            <tbody>
              <tr>
                <td rowSpan={2}>
                  <Image src={image} size='tiny' avatar />
                </td>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Email')}</Header.Subheader>
                    <Header.Content>
                      {(reservation.Guest && reservation.Guest.Email) || '-'}
                    </Header.Content>
                  </Header>
                </td>
              </tr>
              <tr>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Phone')}</Header.Subheader>
                    <Header.Content>
                      {(reservation.Guest && reservation.Guest.Phone) || '-'}
                    </Header.Content>
                  </Header>
                </td>
              </tr>
              <tr>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Status')}</Header.Subheader>
                    <Header.Content>
                      {ReservationStatusTypes[reservation.StatusId]}
                    </Header.Content>
                  </Header>
                </td>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('RoomType')}</Header.Subheader>
                    <Header.Content>
                      {reservation.RoomType?.Name}
                    </Header.Content>
                  </Header>
                </td>
              </tr>
              <tr>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('CheckIn')}</Header.Subheader>
                    <Header.Content>
                      {moment(reservation.CheckInDate).format(dateTimeFormatSpaced)}
                    </Header.Content>
                  </Header>
                </td>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('CheckOut')}</Header.Subheader>
                    <Header.Content>
                      {moment(reservation.CheckOutDate).format(dateTimeFormatSpaced)}
                    </Header.Content>
                  </Header>
                </td>
              </tr>
              <tr>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Nights')}</Header.Subheader>
                    <Header.Content>{reservation.Nights}</Header.Content>
                  </Header>
                </td>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Guests')}</Header.Subheader>
                    <Header.Content>
                      {reservation.Adults +
                        ' / ' +
                        reservation.Children +
                        ' / ' +
                        reservation.Infants}
                    </Header.Content>
                  </Header>
                </td>
              </tr>
              <tr>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Agent')}</Header.Subheader>
                    <Header.Content>
                      {reservation.Agent ? reservation.Agent.Name : '-'}
                    </Header.Content>
                  </Header>
                </td>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Company')}</Header.Subheader>
                    <Header.Content>
                      {reservation.Company ? reservation.Company.Name : '-'}
                    </Header.Content>
                  </Header>
                </td>
              </tr>
              <tr>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Addons')}</Header.Subheader>
                    <Header.Content>
                      {addons.length ? _.join(addons, ', ') : '-'}
                    </Header.Content>
                  </Header>
                </td>
                <td>
                  <Header size='tiny'>
                    <Header.Subheader>{text('Nationality')}</Header.Subheader>
                    <Header.Content>
                      {getNationalityNameById({
                        NationalityId: reservation?.Guest?.NationalityId,
                      }) || '-'}
                    </Header.Content>
                  </Header>
                </td>
              </tr>
              <tr>
                <td colSpan='2'>
                  <Header size='tiny'>
                    <Header.Subheader>
                      {text('Comments')}
                      {canEdit && (
                        <Icon
                          name='pencil'
                          style={{ float: 'right' }}
                          link
                          onClick={() => setShowCommentsEditor(true)}
                        />
                      )}
                    </Header.Subheader>
                    <div style={{ whiteSpace: 'initial' }}>
                      {html.convert(reservation.Comments)}
                    </div>
                  </Header>
                </td>
              </tr>
              <tr>
                <td colSpan='2'>
                  <Header size='tiny'>
                    <Header.Subheader>
                      {text('MessagesForGuests')}
                      {canEdit && (
                        <Icon
                          name='pencil'
                          style={{ float: 'right' }}
                          link
                          onClick={() => setShowGuestCommentsEditor(true)}
                        />
                      )}
                    </Header.Subheader>
                    <div style={{ whiteSpace: 'initial' }}>
                      {html.convert(reservation.CommentsVisibleToGuest)}
                    </div>
                  </Header>
                </td>
              </tr>
              {reservationDocuments?.length ? (
                <tr>
                  <td colSpan='2'>
                    <Accordion panels={documentsPanel} />
                  </td>
                </tr>
              ) : null}
              <tr>
                <td colSpan='2'>
                  <ReservationAddonAccordion
                    ReservationDailyAddons={reservation.ReservationDailyAddons}
                    setShowExtraAddonsPopup={setShowExtraAddonsPopup}
                    initiallyExpended />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Divider />
      </>
    );

    return (
      <React.Fragment>
        <Label
          attached='top'
          size='large'
          style={{ textAlign: 'left', position: 'unset', lineHeight: '100%' }}
        >
          {canEdit && reservation.Guest ? (
            <a
              rel='noreferrer'
              target='_blank'
              href={`${endpoint}CRM/GuestProfile/index/#/profile/${reservation.Guest.Id}`}
              style={{ marginRight: '1em' }}
            >
              {GetFullName(reservation.Guest)}
            </a>
          ) : (
            <span style={{ marginRight: '1em' }}>
              {GetFullName(reservation.Guest)}
            </span>
          )}
          <span style={{ width: '1em' }}></span>
          <Header
            size='tiny'
            target='_blank'
            as={canEdit ? 'a' : 'span'}
            href={`${endpoint}PMS/Reservations/Index/` + reservation.Id}
            content={reservation.Number}
          ></Header>
          {reservation.ConfirmationDate && (
            <Icon color='green' name='check' style={{ marginLeft: 10 }} />
          )}
          <Icon
            name='close'
            style={{ float: 'right', fontSize: '1.2em' }}
            link
            onClick={() => rest.closePanel()}
          />
          {reservation.Guest?.Phone && guestNationality?.Country?.Prefix && (
            <Icon
              link
              className='icon-whatsapp'
              size='large'
              href={getPhoneForWhatsAppLink}
              target='_blank'
              style={{
                color: '#52b563',
                float: 'right',
                fontSize: '1.2em',
                marginRight: '0.5em',
              }}
            />
          )}
          <Popup
            trigger={
              <Icon
                name='print'
                style={{
                  float: 'right',
                  fontSize: '1.2em',
                  marginRight: '0.5em',
                }}
                link
                onClick={() => printReservationSummary()}
              />
            }
            content={text('PrintConfirmation')}
            position='bottom right'
          />
          <Popup
            trigger={
              <Menu.Item
                icon
                link
                href={`${endpoint}/SharedServices/Notes/Index?entityId=${reservation.Id}&entityTypeId=1&$hotelId=${reservation.HotelId}`}
                target='_blank'
                style={{ float: 'right', marginRight: '0.5em' }}
              >
                <Icon
                  className='sticky note'
                  size='large'
                  style={{
                    fontSize: '1.2em',
                  }}
                >
                  {notes.length ? (
                    <Label
                      color='blue'
                      circular
                      size='small'
                      style={{
                        position: 'relative',
                        bottom: '60%',
                        left: '-150%',
                      }}
                    >
                      {notes.length}
                    </Label>
                  ) : null}
                </Icon>
              </Menu.Item>
            }
            content={text('Notes')}
            position='bottom right'
          />
          {isMobileOnly && (
            <Popup
              trigger={
                <Icon
                  color='blue'
                  name={showReservationDetails ? 'triangle up' : 'triangle down'}
                  onClick={() => {
                    setShowReservationDetails((s) => !s);
                    setIsFirstLoad(false);
                  }}
                  style={{
                    float: 'right',
                    fontSize: '2em',
                    marginRight: '.5em',
                  }}
                  link
                />
              }
              open={isFirstLoad ? true : false}
            >
              <Popup.Content>
                {text('ClickToRevealHideReservationDetails')}
              </Popup.Content>
            </Popup>
          )}
        </Label>

        {showReservationDetails && reservationDetails()}
        {/*Constantly open when not on mobile */}

        <div className='totals'>
          {totals && (
            <table>
              <tbody>
                <tr>
                  <td>
                    <Header size='tiny'>
                      <Header.Subheader>{text('TotalPrice')}</Header.Subheader>
                    </Header>
                  </td>
                  <td>
                    <Header size='small'>
                      <Header.Content className='total-price'>

                        <Popup
                          trigger={
                            <a onClick={openReservationPolicyPopup}>
                              <Icon className='icon-handshake' size='big' />
                            </a>
                          }
                          mouseLeaveDelay={500}
                          content={
                            !reservation?.ReservationPolicy?.CancellationPolicies?.length && !reservation?.ReservationPolicy?.DepositPolicies?.length && (!reservation?.ReservationPolicy?.RequiredGtdType || reservation?.ReservationPolicy?.RequiredGtdType === 0)
                              ? message('ReservationPolicyUnassigned')
                              : (
                                <div style={{ padding: 10 }}>
                                  <TextList text={buildAutoDescriptionForPolicy(reservation?.ReservationPolicy, message, text, hotel?.HotelParameter?.TimeZone)} />
                                </div>
                              )
                          }
                        />

                        {formatCurrency(
                          totals.TotalAmountDebitAfterTax,
                          currencyFormat(totals.CurrencyID)
                        )}
                      </Header.Content>
                    </Header>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Header size='tiny'>
                      <Header.Subheader>{text('Tax')}</Header.Subheader>
                    </Header>
                  </td>
                  <td>
                    <Header size='small'>
                      <Header.Content>
                        {formatCurrency(
                          totals.TotalAmountDebitAfterTax -
                          totals.TotalAmountDebitBeforeTax,
                          currencyFormat(totals.CurrencyID)
                        )}
                      </Header.Content>
                    </Header>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Header size='tiny'>
                      <Header.Subheader>
                        {text('TotalPayment')}
                      </Header.Subheader>
                    </Header>
                  </td>
                  <td>
                    <Header size='small'>
                      <Header.Content>
                        {formatCurrency(
                          totals.TotalAmountCreditAfterTax,
                          currencyFormat(totals.CurrencyID)
                        )}
                      </Header.Content>
                    </Header>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Header size='tiny'>
                      <Header.Subheader>
                        {text('TotalBalance')}
                      </Header.Subheader>
                    </Header>
                  </td>
                  <td>
                    <Header size='small' color='red'>
                      <Header.Content>
                        {formatCurrency(
                          totals.TotalAmountDebitAfterTax +
                          totals.TotalAmountCreditAfterTax,
                          currencyFormat(totals.CurrencyID)
                        )}
                      </Header.Content>
                    </Header>
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          <Divider />
        </div>
        <Dimmer.Dimmable
          dimmed={!canEdit}
          style={isMobileOnly ? { height: '100%' } : null}
        >
          <Dimmer.Dimmable dimmed={fabActive}>
            <div className='iconmenu'>
              <a
                target='_blank'
                rel='noopener noreferrer'
                href={`${endpoint}PMS/Reservations/Index/${reservation.Id}?hotelid=${reservation.HotelId}`}
              >
                <Icon className='icon-file' />
                <div>{text('FullReservation')}</div>
              </a>
              <a
                onClick={() =>
                  rest.openShortReservation({
                    ...reservation,
                    _hotel: hotel,
                    totals,
                  })
                }
              >
                <Icon className='icon-file' />
                <div>{text('ShortReservation')}</div>
              </a>
              <a
                target='_blank'
                rel='noopener noreferrer'
                href={`${endpoint}Finance/Folio/Folio?ReservationId=${reservation.Id}&hotelid=${reservation.HotelId}`}
              >
                <Icon className='icon-wallet2' />
                <div>{text('Folio')}</div>
              </a>
            </div>
            <div className='iconmenu'>
              <a onClick={() => checkInReservation()}>
                <Icon className='icon-placeholder-3' />
                <div>{text('CheckIn')}</div>
              </a>
              <a onClick={checkOutReservation}>
                <Icon className='icon-door-exit' />
                <div>{text('CheckOut')}</div>
              </a>
              <a onClick={cancelReservation}>
                <Icon className='icon-delete-1' />
                <div>{text('CancelReservation')}</div>
              </a>
            </div>
            <div className='iconmenu'>
              {hasCCFeature && (
                <a onClick={() => setShowAddCard(true)}>
                  <Icon name='credit card' />
                  <div>{text('PaymentMethods')}</div>
                </a>
              )}
              {reservationGroup?.Id && !reservationGroup.IsPartyGroup ? (
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href={`${endpoint}PMS/ReservationGroupDataService/ReservationGroup?groupid=${reservationGroup.Id}&hotelid=${reservation.HotelId}`}
                >
                  <Icon className='icon-users-relation' style={{ color: reservationGroup.Color || '#cf1b58' }} />
                  <div>{text('GroupReservation')}</div>
                </a>
              ) : (
                <IFrameDialog
                  key='group'
                  title='Family And Friends'
                  showSave={UserHasPermission(
                    PermissionsActions.ViewFamilyAndFriends
                  )}
                  customActions={
                    UserHasPermission(PermissionsActions.ViewFamilyAndFriends) &&
                    printButtonFamilyAndFriends
                  }
                  onSave={onSaveGroup}
                  text={text('FamilyAndFriends')}
                  size='fullscreen'
                  src={`${endpoint}PMS/FamilyAndFriends/Index?MainReservationId=${reservation.Id}&MainReservationNumber=${reservation.Number}&hotelId=${reservation.HotelId}`}
                  trigger={
                    <a>
                      <Icon className='icon-users-relation' style={reservationGroup?.Id ? { color: reservationGroup.Color || '#cf1b58' } : undefined} />
                      <div>{text('FamilyAndFriends')}</div>
                    </a>
                  }
                />
              )}
            </div>
            <Dimmer inverted active={fabActive} />
          </Dimmer.Dimmable>
          {canEdit && (
            <Fab
              event='click'
              id='fab-button'
              style={{
                marginRight: 10,
                bottom: -15,
                right: 22,
                position: 'absolute',
              }}
              icon={<Icon fitted name='plus' />}
              mainButtonStyles={{ backgroundColor: '#01a7bd' }}
              alwaysShowTitle
            >
              <Action text={text('DocumentsUpload')}>
                {/* Additional containing div was added  as the non-native Action component didn't work well with the usage of
									React.ref which resulted in bugs */}
                <div
                  onClick={() => hiddenFileInputRef.current.click()}
                  style={{
                    height: '-webkit-fill-available',
                    width: '-webkit-fill-available',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon className='upload' fitted size='large' />
                  <input
                    type='file'
                    id='file'
                    ref={hiddenFileInputRef}
                    onChange={handleFileUpload}
                    //Resetting DOM's file-input's so onChange can notice the change
                    onClick={(e) => (e.target.value = null)}
                    style={{ display: 'none' }}
                    multiple
                  />
                </div>
              </Action>
              <Action text={text('IssueInvoice')} onClick={issueInvoice}>
                <Icon className='icon-dollar-coins-stack' fitted size='large' />
              </Action>
              <Action
                text={text('Receipt')}
                onClick={() => setShowReceiptPopup(true)}
              >
                <Icon className='icon-wallet' fitted size='large' />
              </Action>
              {reservation && reservation.Guest && reservation.Guest.Email && (
                <Action
                  text={text('SendConfirmation')}
                  onClick={sendConfirmation}
                >
                  <Icon className='icon-envelope-6' fitted size='large' />
                </Action>
              )}
              {hasCheckTaxExemptionFeature && (
                <Action
                  text={text('CheckTaxExemption')}
                  onClick={() => setShowCheckTaxExemptionPopup(true)}
                >
                  <Icon className='vcard' />
                </Action>
              )}
              <Action
                text={text('SendEmail')}
                onClick={() => setShowSendEmail(true)}
              >
                <Icon className='icon-envelope-8' fitted size='large' />
              </Action>

              <Action
                text={text('Messaging')}
                onClick={() => {
                  window.open(
                    `${endpoint}Shell/Messaging?hotelid=${reservation.HotelId}&reservationid=${reservation.Id}`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
              >
                <Icon name='chat' fitted size='large' />
              </Action>
              <Popup
                position='top right'
                key='color'
                on='click'
                text={text('Color')}
                pinned
                trigger={
                  <Action text={text('Color')}>
                    <Icon className='icon-painting-roller' />
                  </Action>
                }
                hoverable
                content={colorPicker()}
              />
              <Action
                text={text('ExtraAddons')}
                onClick={() => setShowExtraAddonsPopup(true)}>
                <Icon className='icon-credit-card' fitted size='large' />
              </Action>
            </Fab>
          )}
          <Dimmer inverted active={!canEdit} />
        </Dimmer.Dimmable>
        <MakePaymentPopup
          actionId={PermissionsActions.AccessSaveCardAuthorization}
          startOpen={showAddCard}
          reservation={reservation}
          onClose={() => setShowAddCard(false)}
          availableActions={[
            PaymentDialog.Actions.SaveCreditCard,
            PaymentDialog.Actions.TakeAuthorization,
          ]}
        />
        <ValidateModalPermission
          open={showReceiptPopup}
          onClose={() => setShowReceiptPopup(false)}
          actionId={PermissionsActions.AccessToCharge}
        >
          <ReceiptPopup
            reservation={reservation}
            startOpen={showReceiptPopup}
            isDefaultSendReceiptByMail={
              hotel.HotelParameter.IsDefaultSendReceiptByMail
            }
            onClose={() => setShowReceiptPopup(false)}
            onPaymentSuccess={refreshTotals}
          />
        </ValidateModalPermission>
        <ValidateModalPermission
          open={showSendEmail}
          onClose={() => setShowSendEmail(false)}
          actionId={PermissionsActions.AccessSendEmailPage}
        >
          <SendMailPopup
            reservation={reservation}
            open={showSendEmail}
            onClose={() => setShowSendEmail(false)}
          />
        </ValidateModalPermission>
        <HtmlEditorPopup
          title={text('ReservationComments')}
          value={reservation ? reservation.Comments : ''}
          startOpen={showCommentsEditor}
          onCancel={() => setShowCommentsEditor(false)}
          onSave={saveComments}
        />
        <HtmlEditorPopup
          title={text('MessagesForGuests')}
          value={reservation ? reservation.CommentsVisibleToGuest : ''}
          startOpen={showGuestCommentsEditor}
          onCancel={() => setShowGuestCommentsEditor(false)}
          onSave={saveGuestComments}
        />
        <CheckTaxExemptionPopup
          guest={reservation.Guest}
          reservation={{
            Id: reservation.Id,
            Number: reservation.Number,
            CheckInDate: reservation.CheckInDate,
            CheckOutDate: reservation.CheckOutDate,
          }}
          hotel={hotel}
          startOpen={showCheckTaxExemptionPopup}
          onClose={() => setShowCheckTaxExemptionPopup(false)}
        />

        <FolioPendingPopup
          error={errorState.error}
          onCancel={() => setErrorState({ showError: false })}
          onConfirm={async () => {
            setErrorState({ showError: false })
            await updateReservationStatus(ReservationStatusesEnums.CheckedOut, true)
          }} />

        {showExtraAddonsPopup && <ExtraAddonsPopup
          open={showExtraAddonsPopup}
          onClose={handleExtraAddonsClose}
          reservation={reservation}
          closeOnDimmerClick={true}
        />}
        {reservationPolicyPopupState.value && <ReservationPolicyPopup
          open={reservationPolicyPopupState.value}
          onClose={closeReservationPolicyPopup}
          reservationId={reservation.Id}
          reservation={reservation}
        />}
        {cancelReservationPopupState.value && <CancelReservationPopup
          open={cancelReservationPopupState.value}
          reservationId={reservation.Id}
          reservation={reservation}
          onClose={handleCancelReservation}
        />}
      </React.Fragment>
    );
  } else {
    return null;
  }
};
ReservationInfo.propTypes = {
  event: PropTypes.object,
  onCreateUpdate: PropTypes.func,
  reservationVisible: PropTypes.bool,
  setSelectedTotals: PropTypes.func,
};
export default ReservationInfo;
